import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, type AnimationProps, motion } from "framer-motion";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { ZIndex } from "~/common/constants";
import { cn } from "~/common/redesign-v1/cn";
import { X } from "~/common/redesign-v1/icons";
import { Button } from "~/common/redesign-v1/ui";
import { Spinner } from "~/components/ui/spinner";
import { useLoadingDelay } from "~/hooks/use-loading-delay";

import redesignWelcomeHero from "./assets/redesign-welcome-hero.png";
import { useCloseToast } from "./toaster";
import { ToastCloseIntent, ToastTypeTag } from "./toasts-api";

/**
 * Welcome notice for the redesign-v1 personal cabinet.
 * Pixel-matched to Figma dialog `5693:49981` (Obra kit copy): hero strip, Stapel titles, Inter body.
 */
const animationProps: AnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export function RedesignWelcomeDialog({ isOpen: isOpenProp }: { isOpen: boolean }) {
  const [isOpen, setIsOpen] = useState(true);
  const { closeToast, isCloseLoading, isLoading } = useCloseToast({
    tag: ToastTypeTag.REDESIGN_V1_WELCOME,
  });
  const isActionLoading = isLoading && !isCloseLoading;
  const isActionLoadingDelayed = useLoadingDelay(isActionLoading, { delay: 200 });
  const isCloseLoadingDelayed = useLoadingDelay(isCloseLoading, { delay: 200 });

  const open = isOpen && isOpenProp;

  function handleAction() {
    void closeToast({ intent: ToastCloseIntent.ACTION });
    setIsOpen(false);
  }

  function handleClose() {
    void closeToast({ intent: ToastCloseIntent.CLOSE });
    setIsOpen(false);
  }

  return (
    <AnimatePresence>
      {open ? (
        <Dialog.Root open={open} onOpenChange={setIsOpen}>
          <Dialog.Portal>
            <motion.div
              {...animationProps}
              className="tw-fixed tw-inset-0"
              style={{ zIndex: ZIndex.CENTERED_DIALOG }}
            >
              <Dialog.Overlay className="tw-fixed tw-inset-0 tw-bg-backdrop" />

              <Dialog.Content
                // Portaled to <body>; mark the panel so redesign tokens resolve (same as CenteredDialog).
                data-redesign-v1=""
                // Radix autofocuses the first focusable (the X) — that paints a focus ring on open.
                onOpenAutoFocus={(event) => event.preventDefault()}
                className={cn(
                  "tw-fixed tw-inset-0 tw-flex tw-flex-col tw-overflow-y-auto tw-border tw-border-solid tw-border-border tw-bg-background tw-font-sans tw-text-foreground tw-shadow-lg tw-outline-none",
                  "tw-rounded-none",
                  // Figma dialog: 762×585, radius 10 (`--rd-radius`).
                  "sm:tw-inset-auto sm:tw-left-1/2 sm:tw-top-1/2 sm:tw-max-h-[90vh] sm:tw-w-full sm:tw-max-w-[762px] sm:-tw-translate-x-1/2 sm:-tw-translate-y-1/2 sm:tw-rounded-rd sm:tw-overflow-hidden",
                )}
                style={{ zIndex: ZIndex.CENTERED_DIALOG + 1 }}
              >
                {/* Dialog Header — 60px row, close aligned end (Figma 5693:49982). */}
                <div className="tw-flex tw-h-[60px] tw-w-full tw-shrink-0 tw-items-center tw-justify-end tw-px-4">
                  <Dialog.Close
                    type="button"
                    className={cn(
                      "tw-inline-flex tw-size-9 tw-min-h-9 tw-min-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-2 tw-text-foreground",
                      "tw-outline-none tw-transition-colors hover:tw-bg-accent",
                      "focus-visible:tw-ring focus-visible:tw-ring-ring/50",
                    )}
                    onClick={handleClose}
                  >
                    <X className="tw-size-4" aria-hidden />
                    <span className="tw-sr-only">
                      <FormattedMessage id="alert.close" />
                    </span>
                  </Dialog.Close>
                </div>

                {/* Hero — pre-cropped Figma export (762×181 @2x). Show the full frame, no extra crop. */}
                <img
                  src={redesignWelcomeHero}
                  alt=""
                  className="tw-block tw-h-[181px] tw-w-full tw-shrink-0 tw-object-cover tw-object-center"
                />

                {/* Copy — px 24 / py 20, 10px gaps (Figma 5693:49985–49991). */}
                <div className="tw-flex tw-w-full tw-flex-col tw-gap-2.5 tw-px-6 tw-py-5">
                  <Dialog.Title
                    className={cn(
                      "tw-m-0 tw-w-full tw-text-center tw-font-rd-accent tw-text-[20px] tw-font-medium tw-leading-[30px] tw-tracking-normal tw-text-stone-800",
                    )}
                  >
                    Добро пожаловать в новый личный кабинет 3D Smile!
                  </Dialog.Title>

                  <Dialog.Description asChild>
                    <p
                      className={cn(
                        "tw-m-0 tw-w-full tw-whitespace-pre-wrap tw-text-center tw-font-sans tw-text-base tw-font-normal tw-leading-6 tw-tracking-normal tw-text-stone-600",
                      )}
                    >
                      {
                        "Долгожданное обновление, в\u00a0которое мы\u00a0вложили много любви и\u00a0тепла: оттачивали каждую линию и\u00a0деталь, советовались с\u00a0лучшими дизайн-специалистами, чтобы\u00a0пространство, в\u00a0котором вы\u00a0работаете, было не\u00a0просто удобным, а\u00a0по\u2011настоящему Вашим\u00a0— располагающим, комфортным и\u00a0элегантным.\n\nЖелаем приятной работы!"
                      }
                    </p>
                  </Dialog.Description>

                  <p
                    className={cn(
                      "tw-m-0 tw-w-full tw-text-center tw-font-rd-accent tw-text-base tw-font-medium tw-leading-[30px] tw-tracking-normal tw-text-stone-800",
                    )}
                  >
                    С заботой о Вас, 3D Smile
                  </p>
                </div>

                {/* Dialog Footer — pt 16 / pb 24 (Figma 5693:50019). */}
                <div className="tw-flex tw-w-full tw-shrink-0 tw-flex-col tw-items-center tw-px-4 tw-pb-6 tw-pt-4">
                  <Button
                    type="button"
                    size="lg"
                    className="tw-min-h-10"
                    disabled={isActionLoadingDelayed || isCloseLoadingDelayed}
                    onClick={handleAction}
                  >
                    {isActionLoadingDelayed ? <Spinner /> : "Начать знакомство"}
                  </Button>
                </div>
              </Dialog.Content>
            </motion.div>
          </Dialog.Portal>
        </Dialog.Root>
      ) : null}
    </AnimatePresence>
  );
}
